import { del, get, patch, post } from '../../api/api'
import { TemplateModel, TemplateShare, TemplateShareScope } from '../../models/TemplateModel'
import { showMessage } from '../../utils/snackbar'
import { useAppStore } from '../../store/appStore'
import { EHRAdapterMap } from '../../adapterConfig'
import i18n from '../../i18n'

interface TemplateModifyResponse {
    name: string
    description: string | null
    keywords: string | null
    items: TemplateModel['items']
    ai_provider: string
}

export class TemplateManager {
    /**
     * Fetches all templates and updates the store.
     */
    public async fetchTemplates(): Promise<void> {
        useAppStore.setState({ templatesLoading: true })
        try {
            const fetchedTemplates = await get<TemplateModel[]>(`/templates`)
            const templateMap = fetchedTemplates.reduce(
                (acc, t) => {
                    acc[t.id] = t
                    return acc
                },
                {} as Record<string, TemplateModel>
            )
            useAppStore.setState({ templates: templateMap })
            await this.fetchFavorites()
        } catch (e) {
            console.error('[TemplateManager] Failed to fetch templates:', e)
            showMessage(i18n.t('errors.fetch_templates'), 'error')
        } finally {
            useAppStore.setState({ templatesLoading: false })
        }
    }

    /**
     * Fetches favorite template IDs and updates the store.
     */
    public async fetchFavorites(): Promise<void> {
        try {
            const favoriteTemplateIds = await get<string[]>('/template-favorites')
            useAppStore.setState({ favoriteTemplateIds })
        } catch (e) {
            console.error('[TemplateManager] Failed to fetch favorite templates:', e)
        }
    }

    /**
     * Toggles a template's favorite status with optimistic update.
     */
    public async toggleFavorite(templateId: string, isFavorite: boolean): Promise<void> {
        const currentFavorites = useAppStore.getState().favoriteTemplateIds
        const newFavorites = isFavorite
            ? currentFavorites.filter((id) => id !== templateId)
            : [...currentFavorites, templateId]

        useAppStore.setState({ favoriteTemplateIds: newFavorites })

        try {
            if (isFavorite) {
                await del(`/template-favorites/${templateId}`)
            } else {
                await post(`/template-favorites/${templateId}`)
            }
        } catch (e) {
            console.error('[TemplateManager] Failed to update favorite status:', e)
            showMessage(i18n.t('errors.update_favorite_failed'), 'error')
            useAppStore.setState({ favoriteTemplateIds: currentFavorites })
        }
    }

    /**
     * Copies a template.
     */
    public async copyTemplate(template: TemplateModel): Promise<void> {
        try {
            const { id: _id, ...templateWithoutId } = template
            const copy = {
                ...templateWithoutId,
                name: `${template.name}${i18n.t('templates.manager.copy_suffix')}`,
            }
            await this.addTemplate(copy)
            showMessage(i18n.t('errors.copy_success'), 'success')
        } catch (err) {
            console.error('[TemplateManager] Error copying template:', err)
            showMessage(i18n.t('errors.copy_template'), 'error')
        }
    }

    private setTemplateShares(templateId: string, shares: TemplateShare[]): void {
        const currentTemplates = useAppStore.getState().templates
        useAppStore.setState({
            templates: {
                ...currentTemplates,
                [templateId]: { ...currentTemplates[templateId], shares },
            },
        })
    }

    /**
     * Shares a template with a group, or updates the scope of an existing share.
     * Requires admin rights on the target group (or org admin).
     */
    public async shareWithGroup(template: TemplateModel, groupId: string, scope: TemplateShareScope): Promise<void> {
        try {
            const shares = await post<TemplateShare[]>(`/templates/${template.id}/groups/${groupId}`, { scope })
            this.setTemplateShares(template.id, shares)
            showMessage(i18n.t('errors.sharing_updated'), 'success')
        } catch (err) {
            console.error('[TemplateManager] Error sharing template with group:', err)
            showMessage(i18n.t('errors.update_sharing'), 'error')
        }
    }

    /**
     * Removes a template share. The owner can always unshare; others need group or org admin rights.
     */
    public async unshareFromGroup(template: TemplateModel, groupId: string): Promise<void> {
        try {
            const shares = await del<TemplateShare[]>(`/templates/${template.id}/groups/${groupId}`)
            this.setTemplateShares(template.id, shares)
            showMessage(i18n.t('errors.sharing_updated'), 'success')
        } catch (err) {
            console.error('[TemplateManager] Error unsharing template from group:', err)
            showMessage(i18n.t('errors.update_sharing'), 'error')
        }
    }

    /**
     * Brings a template's shares to the desired state by adding, rescoping, and removing
     * individual group shares.
     */
    public async applyShares(template: TemplateModel, desired: TemplateShare[]): Promise<void> {
        const currentByGroup = new Map(template.shares.map((s) => [s.group_id, s.scope]))
        const desiredByGroup = new Map(desired.map((s) => [s.group_id, s.scope]))

        try {
            // Additions run before removals: a non-owner's read access may come solely from the
            // shares being removed, and losing it mid-operation would make the backend reject the
            // remaining calls and strand the template without any group (MD-1313).
            let shares = template.shares
            for (const [groupId, scope] of desiredByGroup) {
                if (currentByGroup.get(groupId) !== scope) {
                    shares = await post<TemplateShare[]>(`/templates/${template.id}/groups/${groupId}`, { scope })
                }
            }
            for (const [groupId] of currentByGroup) {
                if (!desiredByGroup.has(groupId)) {
                    shares = await del<TemplateShare[]>(`/templates/${template.id}/groups/${groupId}`)
                }
            }
            this.setTemplateShares(template.id, shares)
            showMessage(i18n.t('errors.sharing_updated'), 'success')
        } catch (err) {
            console.error('[TemplateManager] Error updating template shares:', err)
            showMessage(i18n.t('errors.update_sharing'), 'error')
            await this.fetchTemplates()
        }
    }

    /**
     * Deletes a template and updates the store.
     */
    public async deleteTemplate(templateId: string): Promise<void> {
        const currentTemplates = useAppStore.getState().templates
        const currentFavorites = useAppStore.getState().favoriteTemplateIds
        const currentTemplate = useAppStore.getState().template
        const currentIsTemplatePinned = useAppStore.getState().isTemplatePinned
        const newTemplates = { ...currentTemplates }
        delete newTemplates[templateId]

        // Clear the selection too, or the next encounter creation 404s on a deleted template.
        // The pin flag has to go with it: it otherwise survives and permanently locks the
        // template selector, since it no longer corresponds to any real pinned template.
        const isActiveTemplate = currentTemplate?.id === templateId

        useAppStore.setState({
            templates: newTemplates,
            favoriteTemplateIds: currentFavorites.filter((id) => id !== templateId),
            ...(isActiveTemplate ? { template: null, isTemplatePinned: false } : {}),
        })

        try {
            await del(`/templates/${templateId}`)
            showMessage(i18n.t('errors.template_deleted'), 'success')
        } catch (e) {
            console.error('[TemplateManager] Failed to delete template:', e)
            showMessage(i18n.t('errors.delete_template'), 'error')
            useAppStore.setState({
                templates: currentTemplates,
                favoriteTemplateIds: currentFavorites,
                ...(isActiveTemplate ? { template: currentTemplate, isTemplatePinned: currentIsTemplatePinned } : {}),
            })
        }
    }

    /**
     * Fetches an external template from the specified adapter.
     * @param template The template info containing adapter info and external ID
     * @returns The external TemplateModel
     */
    async getExternalTemplate(template: TemplateModel): Promise<TemplateModel> {
        if (!template.ehr_adapter) {
            throw new Error('Template has no EHR adapter')
        }
        const adapter = EHRAdapterMap[template.ehr_adapter]
        console.info(`[TemplateManager] Fetching external template(${template.external_id}) from adapter: ${adapter}`)
        return await get<TemplateModel>(`adapters/${adapter}/templates/${template.external_id}`)
    }

    /**
     * Imports a template.
     * @param templateData The template data to import
     * @returns The saved TemplateModel
     */
    async importTemplate(templateData: Partial<TemplateModel>): Promise<TemplateModel> {
        const newTemplateData = {
            ...templateData,
            name: `${templateData.name ?? i18n.t('templates.manager.default_imported_name')}${i18n.t('templates.manager.imported_suffix')}`,
        }

        return await this.addTemplate(newTemplateData)
    }

    /**
     * Creates a new template.
     * @param template The template data to create
     * @returns The created TemplateModel
     */
    async addTemplate(template: Partial<TemplateModel>): Promise<TemplateModel> {
        const result = await post<TemplateModel>(`templates`, template)
        const templates = useAppStore.getState().templates

        useAppStore.setState({
            templates: {
                ...templates,
                [result.id]: result,
            },
        })

        return result
    }

    /**
     * Updates an existing template.
     * @param template The template data to update (must contain id)
     * @returns The updated TemplateModel
     */
    async updateTemplate(template: Partial<TemplateModel>): Promise<TemplateModel> {
        if (!template.id) {
            throw new Error('[TemplateManager] Cannot update template without ID')
        }

        const templates = useAppStore.getState().templates
        const patchResult = await patch<Partial<TemplateModel>>(`templates/${template.id}`, template)

        const result: TemplateModel = {
            ...templates[template.id],
            ...patchResult,
        }

        useAppStore.setState({
            templates: {
                ...templates,
                [result.id]: result,
            },
        })

        return result
    }

    /**
     * Applies a user-described change to the template using AI.
     * Stateless on purpose: returns the modified template without saving, so the editor
     * can apply it to its local (possibly unsaved) state and save explicitly.
     * @param template The current editor state
     * @param comment The user's change request
     * @returns The modified name, description, keywords and items (with persistent item ids)
     */
    async modifyTemplateAI(
        template: Pick<TemplateModel, 'name' | 'description' | 'keywords' | 'items'>,
        comment: string
    ): Promise<TemplateModifyResponse> {
        return await post<TemplateModifyResponse>(`/templates/modifications`, {
            name: template.name,
            description: template.description,
            keywords: template.keywords,
            items: template.items,
            restricted_comment: comment,
            temperature: 0.1,
        })
    }

    /**
     * Saves a template (creates or updates).
     * @deprecated Use addTemplate or updateTemplate instead.
     * @param template The template data to save
     * @returns The saved TemplateModel
     */
    async saveTemplate(template: Partial<TemplateModel>): Promise<TemplateModel> {
        if (template.id) {
            return await this.updateTemplate(template)
        } else {
            return await this.addTemplate(template)
        }
    }
}

export const templateManager = new TemplateManager()
